import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Review, ReviewSortOption } from '../types/review'

interface ReviewStore {
  reviews: Review[]
  
  // Actions
  addReview: (review: Omit<Review, 'id' | 'created_at' | 'updated_at' | 'helpful_count' | 'helpful_voters'>) => void
  getProductReviews: (productId: string) => Review[]
  toggleHelpful: (reviewId: string, userId: string) => void
  sortReviews: (reviews: Review[], sortBy: ReviewSortOption) => Review[]
  clearReviews: () => void
}

export const useReviewStore = create<ReviewStore>()(
  persist(
    (set, get) => ({
      reviews: [],

      addReview: (reviewData) => {
        const now = new Date().toISOString()
        const newReview: Review = {
          ...reviewData,
          id: `review-${Date.now()}`,
          helpful_count: 0,
          helpful_voters: [],
          created_at: now,
          updated_at: now,
        }

        set((state) => ({
          reviews: [newReview, ...state.reviews],
        }))
      },

      getProductReviews: (productId) => {
        return get().reviews.filter((r) => r.product_id === productId)
      },

      toggleHelpful: (reviewId, userId) => {
        set((state) => ({
          reviews: state.reviews.map((review) => {
            if (review.id === reviewId) {
              const hasVoted = review.helpful_voters.includes(userId)
              return {
                ...review,
                helpful_count: hasVoted ? review.helpful_count - 1 : review.helpful_count + 1,
                helpful_voters: hasVoted
                  ? review.helpful_voters.filter((id) => id !== userId)
                  : [...review.helpful_voters, userId],
              }
            }
            return review
          }),
        }))
      },

      sortReviews: (reviews, sortBy) => {
        const sorted = [...reviews]
        
        switch (sortBy) {
          case 'most_helpful':
            return sorted.sort((a, b) => b.helpful_count - a.helpful_count)
          case 'most_recent':
            return sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          case 'highest_rating':
            return sorted.sort((a, b) => b.rating - a.rating)
          case 'lowest_rating':
            return sorted.sort((a, b) => a.rating - b.rating)
          default:
            return sorted
        }
      },

      clearReviews: () => set({ reviews: [] }),
    }),
    {
      name: 'BotTola-reviews',
    }
  )
)
